import { ScrollArea } from "@/ui/primitives/scroll-area";
import SidebarNav from "@imminently/decisively-ui/SidebarNav";
import * as React from "react";
import { NavLink, Redirect, Route, Switch, useLocation, useRouteMatch } from "react-router-dom";

import { AlertPage } from "./pages/AlertPage";
import { AttributeFormattersPage } from "./pages/AttributeFormattersPage";
import { AttributePickerPage } from "./pages/AttributePickerPage";
import { AvatarPage } from "./pages/AvatarPage";
import { ButtonPage } from "./pages/ButtonPage";
import { CardPage } from "./pages/CardPage";
import { CardsBlockPage } from "./pages/CardsBlockPage";
import { CheckboxPage } from "./pages/CheckboxPage";
import { ChipPage } from "./pages/ChipPage";
import { DatePickerPage } from "./pages/DatePickerPage";
import { DateTimePickerPage } from "./pages/DateTimePickerPage";
import { DetailPage } from "./pages/DetailPage";
import { DialogPage } from "./pages/DialogPage";
import { DismissGuardPage } from "./pages/DismissGuardPage";
import { ExistingResourceSelectPage } from "./pages/ExistingResourceSelectPage";
import { FileEmbedPage } from "./pages/FileEmbedPage";
import { GoalMenuPage } from "./pages/GoalMenuPage";
import { GraphDataPage } from "./pages/GraphDataPage";
import { GraphNodePage } from "./pages/GraphNodePage";
import { GuidedTourPage } from "./pages/GuidedTourPage";
import { IconsPage } from "./pages/IconsPage";
import { InputPage } from "./pages/InputPage";
import { ItemListPage } from "./pages/ItemListPage";
import { NodePathPage } from "./pages/NodePathPage";
import { NodeValuePage } from "./pages/NodeValuePage";
import { ProgressPage } from "./pages/ProgressPage";
import { RadioPage } from "./pages/RadioPage";
import { ResourceLinkPage } from "./pages/ResourceLinkPage";
import { RichTextEditorPage } from "./pages/RichTextEditorPage";
import { RuleExplanationPage } from "./pages/RuleExplanationPage";
import { SelectPage } from "./pages/SelectPage";
import { SetupListPage } from "./pages/SetupListPage";
import { SteppedProgressPage } from "./pages/SteppedProgressPage";
import { SwitchPage } from "./pages/SwitchPage";
import { TablesPage } from "./pages/TablesPage";
import { TabsPage } from "./pages/TabsPage";
import { TimePickerPage } from "./pages/TimePickerPage";
import { ToastsPage } from "./pages/ToastsPage";

const components = [
  { name: "Alert", path: "alert", component: AlertPage },
  { name: "Avatar", path: "avatar", component: AvatarPage },
  { name: "Button", path: "button", component: ButtonPage },
  { name: "Checkbox", path: "checkbox", component: CheckboxPage },
  { name: "Chip", path: "chip", component: ChipPage },
  { name: "Input", path: "input", component: InputPage },
  { name: "Item List", path: "item-list", component: ItemListPage },
  { name: "Progress", path: "progress", component: ProgressPage },
  { name: "Select", path: "select", component: SelectPage },
  { name: "Existing Resource Select", path: "existing-resource-select", component: ExistingResourceSelectPage },
  { name: "Setup List", path: "setup-list", component: SetupListPage },
  { name: "Switch", path: "switch", component: SwitchPage },
  { name: "Card", path: "card", component: CardPage },
  { name: "Dialog", path: "dialog", component: DialogPage },
  { name: "Date Picker", path: "date-picker", component: DatePickerPage },
  { name: "Time Picker", path: "time-picker", component: TimePickerPage },
  { name: "Date Time Picker", path: "date-time-picker", component: DateTimePickerPage },
  { name: "Detail", path: "detail", component: DetailPage },
  { name: "Toasts", path: "toasts", component: ToastsPage },
  { name: "Radio", path: "radio", component: RadioPage },
  { name: "Tables", path: "tables", component: TablesPage },
  { name: "Tabs", path: "tabs", component: TabsPage },
  { name: "Icons", path: "icons", component: IconsPage },
  { name: "Dismiss Guard", path: "dismiss-guard", component: DismissGuardPage },
  { name: "Rich Text Editor", path: "rich-text-editor", component: RichTextEditorPage },
].sort((a, b) => a.name.localeCompare(b.name));

const blocks = [
  { name: "File Embeds", path: "file-embeds", component: FileEmbedPage },
  { name: "Guided Tour", path: "guided-tour", component: GuidedTourPage },
  { name: "Resource Cards", path: "cards", component: CardsBlockPage },
  { name: "Resource Links", path: "resource-links", component: ResourceLinkPage },
  { name: "Stepped Progress", path: "stepped-progress", component: SteppedProgressPage },
  { name: "Node Value", path: "node-value", component: NodeValuePage },
  { name: "Node Path", path: "node-path", component: NodePathPage },
  { name: "Goal Menu", path: "goal-menu", component: GoalMenuPage },
  { name: "Graph Node", path: "graph-node", component: GraphNodePage },
  { name: "Graph Data", path: "graph-data", component: GraphDataPage },
  { name: "Rule Explanation", path: "rule-explanation", component: RuleExplanationPage },
  { name: "Attribute Picker", path: "attribute-picker", component: AttributePickerPage },
  { name: "Attribute Formatters", path: "attribute-formatters", component: AttributeFormattersPage },
];

export default function DesignSystem() {
  const { path, url } = useRouteMatch();
  const location = useLocation();

  return (
    <div className="flex h-full bg-background">
      <SidebarNav
        collapsed={false}
        width={256}
        theme="light"
      >
        <SidebarNav.Panel className="border-r border-border">
          <SidebarNav.Header className="p-6">
            <h1 className="text-xl font-bold">Design System</h1>
            <p className="text-sm text-muted-foreground mt-1">Design components</p>
          </SidebarNav.Header>
          <SidebarNav.Content>
            <ScrollArea className="h-full">
              <SidebarNav.Group>
                <SidebarNav.GroupLabel>Components</SidebarNav.GroupLabel>
                <SidebarNav.GroupContent>
                  <SidebarNav.Menu>
                    {components.map((component) => {
                      const isActive =
                        location.pathname === `${url}/${component.path}` ||
                        location.pathname.startsWith(`${url}/${component.path}/`);
                      return (
                        <SidebarNav.MenuItem key={component.path}>
                          <SidebarNav.MenuButton
                            asChild
                            isActive={isActive}
                          >
                            <NavLink to={`${url}/${component.path}`}>
                              <span>{component.name}</span>
                            </NavLink>
                          </SidebarNav.MenuButton>
                        </SidebarNav.MenuItem>
                      );
                    })}
                  </SidebarNav.Menu>
                </SidebarNav.GroupContent>
              </SidebarNav.Group>

              <SidebarNav.Group>
                <SidebarNav.GroupLabel>Blocks</SidebarNav.GroupLabel>
                <SidebarNav.GroupContent>
                  <SidebarNav.Menu>
                    {blocks.map((block) => {
                      const isActive =
                        location.pathname === `${url}/blocks/${block.path}` ||
                        location.pathname.startsWith(`${url}/blocks/${block.path}/`);
                      return (
                        <SidebarNav.MenuItem key={block.path}>
                          <SidebarNav.MenuButton
                            asChild
                            isActive={isActive}
                          >
                            <NavLink to={`${url}/blocks/${block.path}`}>
                              <span>{block.name}</span>
                            </NavLink>
                          </SidebarNav.MenuButton>
                        </SidebarNav.MenuItem>
                      );
                    })}
                  </SidebarNav.Menu>
                </SidebarNav.GroupContent>
              </SidebarNav.Group>
            </ScrollArea>
          </SidebarNav.Content>
        </SidebarNav.Panel>
      </SidebarNav>

      {/* Main content */}
      <main className="flex-1 min-h-0">
        <ScrollArea className="h-full">
          <div className="p-8 max-w-5xl">
            <Switch>
              <Route
                exact
                path={path}
                render={() => <Redirect to={`${url}/${components[0].path}`} />}
              />
              {components.map((component) => (
                <Route
                  key={component.path}
                  path={`${path}/${component.path}`}
                  component={component.component}
                />
              ))}
              {blocks.map((block) => (
                <Route
                  key={block.path}
                  path={`${path}/blocks/${block.path}`}
                  component={block.component}
                />
              ))}
            </Switch>
          </div>
        </ScrollArea>
      </main>
    </div>
  );
}
